// ── capstone-submissions-section: the LIVE admin submissions list (F15 READ) ─
// Reads the NOW-LIVE GET /cohorts/:cohort_id/capstone-submissions
// (coaching.reports.view.org, scope-isolated bare { capstoneSubmissions:[…] }) via
// useHonestQuery. A cohort collection filter drives the read (the admin picks a
// cohort from the scoped cohort list — the server then scope-isolates the rows).
// The 9 honest postures stay: loading is a skeleton, an empty cohort shows the
// neutral empty state, a denied/unavailable read renders its honest state — never
// a blank.
//
// reportId is a REFERENCE only — we render its presence ("report linked") and do
// NOT render a report body here (the report lives behind the report routes).
//
// ── PARTICIPANT ISOLATION (release gate) ─────────────────────────────────────
// This is the ADMIN surface (staff/coach/company_admin/cohort_lead). The server
// scope-isolates rows; a participant never reaches this admin page (route-denied)
// and sees ONLY their own submission on their dashboard. We never present a
// cross-participant roster affordance to a participant here.

import { CollectionPagination, CollectionToolbar, DataTableV2, Module, ModuleBody, ModuleHead } from "@vjal/ui";
import { useEffect, useMemo, useState } from "react";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices, getCoachingReadinessServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type { CoachingCohortDto, ListResponse } from "../../services/coaching-dtos";
import type { CoachingCapstoneSubmissionsResponseDto } from "../../services/coaching-readiness-dtos";
import { readDirectoryInitialState } from "../directory-collection-state";
import {
  CAPSTONE_PAGE_SIZE,
  cohortOptionsFromCohorts,
  deriveInitialCapstoneCohortId,
  useCapstoneCollection,
  type CapstoneSubmissionRow
} from "./capstone-collection";

const services = getCoachingServices();
const readiness = getCoachingReadinessServices();

export function CapstoneSubmissionsSection({ routePath }: { routePath?: string }) {
  const cohortsQuery = useHonestQuery<ListResponse<CoachingCohortDto>>(
    coachingQueryKeys.list("cohorts.list"),
    () => services.cohorts.list()
  );
  const cohortOptions = useMemo(() => cohortOptionsFromCohorts(cohortsQuery.data?.items ?? []), [cohortsQuery.data?.items]);
  const routeSeed = useMemo(() => readDirectoryInitialState(routePath, CAPSTONE_PAGE_SIZE), [routePath]);
  const [activeCohortId, setActiveCohortId] = useState(() =>
    deriveInitialCapstoneCohortId(cohortOptionsFromCohorts([]), routeSeed)
  );

  // Render adjustments, not effects (bug-pack class, 2026-07-06): apply the
  // derived initial cohort when it CHANGES (posture settled / options / seed).
  const initialCohortId =
    cohortsQuery.posture === null ? deriveInitialCapstoneCohortId(cohortOptions, routeSeed) : "";
  const [prevInitialCohortId, setPrevInitialCohortId] = useState("");
  if (initialCohortId !== prevInitialCohortId) {
    setPrevInitialCohortId(initialCohortId);
    if (initialCohortId && initialCohortId !== activeCohortId) setActiveCohortId(initialCohortId);
  }

  const resolvedCohortId =
    activeCohortId || (cohortsQuery.posture === null ? (cohortOptions[0]?.id ?? "") : "");

  const submissionsQuery = useHonestQuery<CoachingCapstoneSubmissionsResponseDto>(
    coachingQueryKeys.readiness.capstoneForCohort(resolvedCohortId || "missing"),
    () => readiness.capstone.listForCohort(resolvedCohortId),
    {
      enabled: Boolean(resolvedCohortId),
      disabledPosture: "loading",
      isEmpty: (data) => (data.capstoneSubmissions ?? []).length === 0
    }
  );

  const submissionRows = useMemo(
    () => (submissionsQuery.data?.capstoneSubmissions ?? []) as CapstoneSubmissionRow[],
    [submissionsQuery.data]
  );
  const collection = useCapstoneCollection(submissionRows, cohortOptions, routePath);
  const filterCohortId = collection.state.filters.cohort_id as string | undefined;
  const [prevFilterCohortId, setPrevFilterCohortId] = useState(filterCohortId);
  if (filterCohortId !== prevFilterCohortId) {
    setPrevFilterCohortId(filterCohortId);
    if (filterCohortId && filterCohortId !== activeCohortId) setActiveCohortId(filterCohortId);
  }

  useEffect(() => {
    if (activeCohortId && collection.state.filters.cohort_id !== activeCohortId) {
      collection.setFilter("cohort_id", activeCohortId);
    }
  }, [activeCohortId, collection]);

  if (cohortsQuery.posture === "loading") {
    return (
      <Module span={12}>
        <ModuleHead title="Capstone submissions" subtitle="Loading scoped cohorts for capstone submissions." />
        <ModuleBody data-testid="capstone-submissions-list">
          <div data-honest-posture="loading">
            <HonestState posture="loading" loadingVariant="rows" />
          </div>
        </ModuleBody>
      </Module>
    );
  }

  if (cohortsQuery.posture !== null || cohortOptions.length === 0) {
    return (
      <Module span={12}>
        <ModuleHead title="Capstone submissions" subtitle="Pick a cohort to view its capstone submissions." />
        <ModuleBody data-testid="capstone-submissions-list">
          <div data-honest-posture={cohortsQuery.posture ?? "empty"}>
            <HonestState
              posture={cohortsQuery.posture ?? "empty"}
              gate={cohortsQuery.gate}
              reference={cohortsQuery.reference}
              onRetry={cohortsQuery.retry}
              loadingVariant="rows"
              title={cohortsQuery.posture === "empty" ? "No cohorts yet" : undefined}
              message={cohortsQuery.posture === "empty" ? "Capstone submissions appear once a cohort exists." : undefined}
            />
          </div>
        </ModuleBody>
      </Module>
    );
  }

  const allRows = submissionRows;
  const posture =
    submissionsQuery.posture === null && collection.total === 0 && allRows.length > 0
      ? "empty"
      : submissionsQuery.posture;
  const gated = posture !== null;
  const showToolbar = submissionsQuery.posture === null || posture === "empty";

  return (
    <Module span={12}>
      <ModuleHead title="Capstone submissions" subtitle="Scope-isolated to your access; reportId is a reference only." />
      <ModuleBody className="space-y-4" data-testid="capstone-submissions-list">
        {showToolbar ? (
          <CollectionToolbar
            collection={collection}
            searchPlaceholder="Search by submission or submitter…"
            className="rounded-xl border border-line"
            data-testid="capstone-submissions-toolbar"
            stableChipNames
          />
        ) : null}

        <div className="rounded-xl border border-line bg-white">
          {gated ? (
            <div data-honest-posture={posture ?? "loading"}>
              <HonestState
                posture={posture ?? "loading"}
                gate={submissionsQuery.gate}
                reference={submissionsQuery.reference}
                onRetry={submissionsQuery.retry}
                loadingVariant="rows"
                title={posture === "empty" ? "No submissions yet" : undefined}
                message={
                  posture === "empty"
                    ? "No capstone submissions for this cohort yet. A staff intake enqueues evaluation; results appear once the agent lane is enabled."
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              {/* Mobile residual (U-COACH-RESIDUAL-C1 Gemini): Submitter header/cells
                  hard-clip mid-content without scroll affordance. Outer scroller owns
                  overflow-x + scroll-hint; force w-max min-width so columns scroll. */}
              <div className="relative min-w-0 max-w-full overflow-x-auto overscroll-x-contain shadow-scroll-hint">
                <div className="w-max min-w-[48rem]" data-testid="capstone-submissions-table-scroller">
                  <DataTableV2
                    collection={collection}
                    caption="Capstone submissions for the selected cohort"
                    emptyTitle="No submissions match your search"
                    emptyMessage="Clear or change the filters above to widen the view."
                    className="min-w-[48rem] border-0 shadow-none"
                    tableClassName="w-max min-w-[48rem] max-w-none [&_th]:whitespace-nowrap [&_td]:max-w-none [&_td]:whitespace-nowrap"
                  />
                </div>
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent sm:hidden"
                />
              </div>
              {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
            </>
          )}
        </div>
      </ModuleBody>
    </Module>
  );
}
